import type { PublicStatus } from '@oneapp/shared';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { adminKeys, statusApi } from '../admin/api';
import { Icon } from '../components/icons';

/** The status page's words for what's wrong: "The GST portal is slow", and what it means for them. */
export function useStatusHeadline() {
  const { t } = useTranslation();
  return (status: PublicStatus) => {
    const problem = status.problem;
    if (!problem) return { title: t('status.allNormal'), text: null as string | null };
    return {
      title: t(`status.headline.${problem.component}.${problem.state}`),
      text: problem.message ?? t(`status.means.${problem.component}.${problem.state}`),
    };
  };
}

/** The chat's line when something's wrong (gap 7): what, and a link to the status page. */
export function StatusLine() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const headline = useStatusHeadline();
  const status = useQuery({
    queryKey: adminKeys.publicStatus,
    queryFn: ({ signal }) => statusApi.get(signal),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });
  const data = status.data;
  if (!data?.problem) return null;
  const shown = headline(data);
  return (
    <button type="button" className={`status-line status-line--${data.state}`} onClick={() => navigate('/status')}>
      <Icon name="alert-triangle" size={16} />
      <span className="status-line__text">
        <strong>{shown.title}.</strong> {shown.text}
      </span>
      <span className="status-line__more">{t('status.more')}</span>
    </button>
  );
}
